import {
  Article,
  CreateInvoiceArticle,
  CreateInvoiceInput,
  CreateOrPatchMetadata,
  CreateOrPatchTax,
  CreateOrPutCustomerInput,
  CreateOrPutRevenueTypeInput,
  Customer,
  Invoice,
  InvoiceArticle,
  InvoiceBulkImportObject,
  InvoiceCSV,
  Metadata,
  PatchArticle,
  PatchInvoiceArticle,
  PatchInvoiceInput,
  RevenueType,
  State,
  Tax,
  Department,
  CreateOrPutDepartmentInput
} from '@thro-types/billing';

import { AxiosError, AxiosRequestConfig } from 'axios';
import { Gateway } from 'index';

import { PDFGeneratorData } from './hooks/billing/pdfTypes';

interface ListResponse<T> {
  data: T[];
  offset?: number;
  limit?: number;
  count: number;
  dataCount: number;
}

export interface UpdateInterface<T> {
  data: T;
  key: string;
}

export const getCustomers = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<ListResponse<Customer>>('/billing/v1/customers', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getCustomer = async (id: string, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Customer>(`/billing/v1/customers/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getCustomerWithInvoices = async (id: string, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Customer & { invoices: ListResponse<Invoice> }>(
      `/billing/v1/customers/${id}/invoices`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createCustomer = async (
  createCustomer: CreateOrPutCustomerInput,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.post<Customer>('/billing/v1/customers', createCustomer, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const importCustomers = async (data: FormData, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<Customer[]>('/billing/v1/customers/import', data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const importInvoicesBulk = async (data: FormData, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<InvoiceBulkImportObject>(
      '/billing/v1/invoices/bulk-import',
      data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getPdfFilePathForPreview = async (
  data: PDFGeneratorData,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.post<Blob>('/billing/v1/invoice-pdfs/create-invoice', data, {
      ...config,
      responseType: 'blob'
    });
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const importInvoicesBulkFromDB = async (id: string, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<string[]>(
      `/billing/v1/invoices/bulk-import/importCSVFileFromDB/${id}`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getInvoice = async (id: string, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Invoice>(`/billing/v1/invoices/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getInvoices = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<ListResponse<Invoice>>('/billing/v1/invoices', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getCSVInvoices = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<ListResponse<InvoiceCSV>>('/billing/v1/exports', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getMyInvoices = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<ListResponse<Invoice>>('/billing/v1/invoices/my', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getMyInvoicesCounts = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<{ draft: number; open: number }>(
      '/billing/v1/invoices/myCounts',
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createInvoice = async (
  createInvoice: CreateInvoiceInput,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.post<Invoice>('/billing/v1/invoices', createInvoice, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateInvoice = async (
  id: string,
  updateInvoice: PatchInvoiceInput,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<Invoice>(
      `/billing/v1/invoices/${id}`,
      updateInvoice,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateInvoiceCustomer = async (
  invoiceId: string,
  updateInvoice: CreateOrPutCustomerInput,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.post<Customer>(
      `/billing/v1/customers/${invoiceId}/invoices`,
      updateInvoice,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateInvoiceState = async (
  state: keyof typeof State,
  invoiceIds: any,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<Invoice[]>(
      `/billing/v1/invoices/state/${state}`,
      { invoiceIds },
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const cancelInvoice = async (id: string, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<Invoice>(
      `/billing/v1/invoices/${id}/cancel`,
      undefined,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const deleteInvoice = async (id: string, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.delete<Invoice>(`/billing/v1/invoices/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateCustomer = async (
  data: UpdateInterface<CreateOrPutCustomerInput>,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.put<Customer>(
      `/billing/v1/customers/${data.key}`,
      data.data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getArticle = async (id: string, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Article>(`/billing/v1/articles/${id}`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getArticles = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<ListResponse<Article>>('/billing/v1/articles', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateArticle = async (
  sku: string,
  data: PatchArticle,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.put<Article>(`/billing/v1/articles/${sku}`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createInvoiceArticle = async (
  invoiceId: string,
  createInvoiceArticle: CreateInvoiceArticle,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.post<CreateInvoiceArticle, { data: Invoice[] }>(
      `/billing/v1/invoices/${invoiceId}/articles`,
      createInvoiceArticle,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateInvoiceArticle = async (
  invoiceId: string,
  id: string,
  updateInvoiceArticle: PatchInvoiceArticle,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.patch<Invoice[]>(
      `/billing/v1/invoices/${invoiceId}/articles/${id}`,
      updateInvoiceArticle,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const deleteInvoiceArticle = async (
  invoiceId: string,
  id: string,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.delete<InvoiceArticle>(
      `/billing/v1/invoices/${invoiceId}/articles/${id}`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getTaxes = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<ListResponse<Tax>>('/billing/v1/tax', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createTaxes = async (data: CreateOrPatchTax, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<Tax>('/billing/v1/tax', data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateTaxes = async (
  data: UpdateInterface<CreateOrPatchTax>,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.put<Tax>(`/billing/v1/tax/${data.key}`, data.data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getMetadata = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<ListResponse<Metadata>>('/billing/v1/metadata', config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createMetadata = async (data: CreateOrPatchMetadata, config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.post<Metadata>('/billing/v1/metadata', data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateMetadata = async (
  data: UpdateInterface<CreateOrPatchMetadata>,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.put<Metadata>(
      `/billing/v1/metadata/${data.key}`,
      data.data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getRevenueTypes = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<ListResponse<RevenueType>>(
      `/billing/v1/revenuetype`,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createRevenueType = async (
  data: CreateOrPutRevenueTypeInput,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.post<RevenueType>(`/billing/v1/revenuetype`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateRevenueType = async (
  data: UpdateInterface<CreateOrPutRevenueTypeInput>,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.put<RevenueType>(
      `/billing/v1/revenuetype/${data.key}`,
      data.data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getDepartments = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<ListResponse<Department>>(`/billing/v1/department`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const getDepartmentsWithInvoices = async (config?: AxiosRequestConfig) => {
  try {
    const response = await Gateway.get<Department[]>(`/billing/v1/department/hasInvoices`, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const createDepartment = async (
  data: CreateOrPutDepartmentInput,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.post<Department>(`/billing/v1/department`, data, config);
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};

export const updateDepartment = async (
  data: UpdateInterface<CreateOrPutDepartmentInput>,
  config?: AxiosRequestConfig
) => {
  try {
    const response = await Gateway.put<Department>(
      `/billing/v1/department/${data.key}`,
      data.data,
      config
    );
    return response.data;
  } catch (error) {
    if (error instanceof AxiosError) {
      throw error;
    }
    throw new AxiosError();
  }
};
